import { useMemo } from 'react';
import styled from 'styled-components';

import { ListingType } from 'algolia-api/types/ListingType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { StaffTypeFilter } from 'algolia-api/types/StaffTypeFilter';
import { Box } from 'components/Box';
import { PrimaryButton } from 'components/Button';
import { Icon } from 'components/Icon';
import { ListingTypeSelect } from 'components/Listing/ListingTypeSelect';
import SearchLocationInputContainer from 'containers/SearchLocationInputContainer';
import { SearchQueryInputContainer } from 'containers/SearchQueryInputContainer';
import { TabContext } from 'containers/TabContext';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { colors, cssBreakpoints, mediaRetina, transition } from 'theme';
import { scrollToTop } from 'utils/scroll';

const StyledListingTypeSelect = styled(ListingTypeSelect)`
  height: 50px;
  && {
    select {
      height: 50px;
    }

    @media all and (min-width: ${cssBreakpoints.mdUp}) {
      border-radius: 8px;
      border: 1px solid transparent;
      background: ${colors.inputGrey};
      .react-select__control {
        border: solid 1px #c4c8cc;
        border-radius: 8px 0 0 8px;
      }
    }
  }
  & + * {
    svg {
      margin-top: 2px;
    }
  }
`;

const SearchButton = styled(PrimaryButton)`
  height: 50px;

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    padding: 0 40px;
  }
`;

const StyledSearchInputs = styled.form<{
  $isTabbing?: boolean;
}>`
  && {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin: 0 auto;
    padding: 0;
    font-size: 18px;
    @media all and (min-width: ${cssBreakpoints.smUp}) {
      max-width: 400px;
    }
    @media all and (min-width: ${cssBreakpoints.mdUp}) {
      max-width: none;
    }

    display: flex;
    @media all and (min-width: ${cssBreakpoints.mdUp}) {
      flex-direction: row;
      gap: 0;
    }

    > * {
      border: 0 !important;
      box-sizing: border-box;
      margin-bottom: 0;
      @media all and (min-width: ${cssBreakpoints.mdUp}) {
        margin-left: 8px;
        margin-top: 0;
        height: 50px;

        &:first-child {
          .react-select__input {
            opacity: 0;
          }
        }
        .react-select__option {
          height: 49px;
          border-bottom-color: ${colors.selectionGrey};
        }
      }
    }

    .react-select__control {
      height: 100%;
      border-radius: 8px;
      border: 0;
      overflow: visible;
      transition: all ${transition};
      background: ${colors.inputGrey};
    }

    @media all and (max-width: ${cssBreakpoints.mdDown}) {
      > *,
      .react-select__control {
        width: 100%;
        max-width: none;
      }
    }

    input,
    button,
    .react-select__value-container,
    .react-select__control {
      font-size: inherit;
    }
    .react-select__value-label {
      color: ${colors.brandBlue} !important;
    }
    .react-select__option {
      height: 49px;
    }
    .react-select__menu {
      max-height: none;
      margin-top: 3px;
      box-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
      border: 0;
    }
    .react-select__single-value {
      color: ${colors.brandBlue};
    }
    .react-select__control--menu-is-open {
      filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.1));
    }
    .react-select__control--is-focused {
      border-color: transparent;
      box-shadow: 0 0 0 1px ${colors.brandBlue} inset;
      ${mediaRetina} {
        box-shadow: 0 0 0 1.5px ${colors.brandBlue} inset;
      }
    }
    ${(props) =>
      !props.$isTabbing
        ? `
      .search-inputs-location-container input {
        &:focus {
          outline: 0;
        }
      }
    `
        : `
    `}
  }
`;

type DisplayedType = ListingType | StaffTypeFilter;

type Props = {
  selectedType: DisplayedType;
  listingTypes: Array<DisplayedType>;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSearch: (...args: Array<any>) => any;
  query: string;
  searchLocation: SearchLocation | null | undefined;
  searchLocationDisabled?: boolean;
  setListingType: (arg0: ListingType) => void;
  setQuery: (arg0: string) => void;
  onLocationChange: (arg0: SearchLocation | null | undefined) => void;
  search: (arg0: string) => void;
  clearSearchInput: () => void;
};

export function PageHeaderSearchInputs({
  selectedType,
  listingTypes,
  onSearch,
  query,
  searchLocation,
  searchLocationDisabled,
  setListingType,
  setQuery,
  onLocationChange,
  search,
  clearSearchInput,
}: Props) {
  const { isEnvironmentLoaded } = useUserEnvironment();
  const locationInput = useMemo(
    () => (
      <SearchLocationInputContainer
        prefixVariant="contrast"
        styleVariant="contrast"
        disabled={searchLocationDisabled}
        showLocationIcon
        showClearButton
        placeholder={getText('Everywhere')}
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        locationText={searchLocation && searchLocation.text}
        onChange={onLocationChange}
      />
    ),
    [searchLocation, searchLocationDisabled, onLocationChange],
  );
  return (
    <TabContext.Consumer>
      {(isTabbing) => (
        <StyledSearchInputs
          onSubmit={(e) => {
            e.preventDefault();
            search(query);

            if (onSearch) {
              onSearch();
            }
          }}
          data-qa-id="search-inputs"
          $isTabbing={isTabbing}
        >
          <Box component="label" display="block" flex="1">
            <Box display="none">{getText('Select listing type')}</Box>
            <StyledListingTypeSelect
              value={selectedType}
              onChange={setListingType}
              listingTypes={listingTypes}
            />
          </Box>

          <Box height="100%" width="100%" flex={[null, null, null, '2']}>
            <SearchQueryInputContainer
              query={query}
              placeholder={getText('Search by keyword, skill, or interest')}
              xsPlaceholder={getText('Keyword, skill, or interest')}
              data-qa-id="search-input"
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              clearQuery={clearSearchInput}
              presentation="contrast"
            />
          </Box>

          <Box flex="1">{locationInput}</Box>

          <Box
            flex="0 0 auto"
            height="100%"
            display={['block', null, null, 'flex']}
          >
            <SearchButton
              disabled={!isEnvironmentLoaded}
              fullWidth
              type="submit"
              data-qa-id="search-button"
              onClick={scrollToTop}
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <Icon
                  name="search"
                  size={20}
                  keepColorOnLoad
                  initialColorName="white"
                />
                <Box ml="7px" mr="4px">
                  {getText('Search')}
                </Box>
              </Box>
            </SearchButton>
          </Box>
        </StyledSearchInputs>
      )}
    </TabContext.Consumer>
  );
}
